/**
 * اجزای ظاهری پایه: کارت، سربرگ، badge، سنجه، بنر، حالت خالی، اسکلتون.
 *
 * این‌ها نازک‌اند و روی عنصر بومی می‌نشینند. هر جزئی که رفتار تعاملی پیچیده
 * دارد (دیالوگ، تب، select) روی `radix-ui` سوار است و اینجا نیست.
 *
 * قاعده‌ی ظاهری مشترک: هر سطحِ برجسته مرز نازک دارد و یکی از سایه‌های توکن‌شده
 * (`shadow-raised` یا `shadow-lifted`). خودِ آن توکن‌ها هایلایت یک‌پیکسلیِ لبه‌ی
 * بالا را هم در خود دارند؛ همین است که کارت را از یک مستطیل سفید جدا می‌کند.
 */

import { cva, type VariantProps } from 'class-variance-authority'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { fa, faPercent } from '@/lib/format'

// ===================================================================
// کارت
// ===================================================================

const cardStyles = cva('rounded-lg border', {
  variants: {
    tone: {
      /** سطح معمول محتوا. */
      raised: 'border-line bg-surface-raised shadow-raised',
      /** سطح فرورفته برای گروه‌بندی درون کارت، بدون سایه. */
      sunken: 'border-line bg-surface-sunken',
      /** کارت شاخص با هاله‌ی لهجه در گوشه. برای حکم و ناحیه‌ی اصلی صفحه. */
      feature:
        'relative overflow-hidden border-accent-line/70 bg-surface-raised shadow-lifted',
      /** شیشه‌ای، برای لایه‌های شناور روی محتوا. */
      glass: 'border-line glass shadow-overlay',
    },
  },
  defaultVariants: { tone: 'raised' },
})

interface CardProps
  extends ComponentPropsWithoutRef<'section'>,
    VariantProps<typeof cardStyles> {
  /** بلند شدن نامحسوس در hover. فقط برای کارت قابل کلیک. */
  interactive?: boolean
}

export const Card = ({
  className,
  tone,
  interactive = false,
  children,
  ...rest
}: CardProps) => (
  <section
    className={cn(cardStyles({ tone }), interactive && 'lift cursor-pointer', className)}
    {...rest}
  >
    {tone === 'feature' && (
      // هاله‌ی گوشه با `pointer-events-none` تا کلیک را نگیرد.
      <span
        className="pointer-events-none absolute -top-24 -end-16 size-64 rounded-full bg-accent/10 blur-3xl"
        aria-hidden
      />
    )}
    {children}
  </section>
)

// ===================================================================
// نشان آیکون — قاب کوچک رنگی دور آیکون
// ===================================================================

const chipStyles = cva(
  'inline-flex shrink-0 items-center justify-center rounded-md ring-1 ring-inset',
  {
    variants: {
      tone: {
        accent: 'bg-accent-soft text-accent ring-accent-line',
        support: 'bg-support-soft text-support ring-support/25',
        oppose: 'bg-oppose-soft text-oppose ring-oppose/25',
        conditional: 'bg-conditional-soft text-conditional ring-conditional/30',
        neutral: 'bg-surface-sunken text-ink-muted ring-line',
        /** گرادیان لهجه با متن روشن. در هر صفحه یکی، وگرنه تأکید بی‌معنا می‌شود. */
        solid:
          'bg-linear-100 from-accent to-accent-two text-ink-inverse ring-transparent shadow-glow',
      },
      size: {
        sm: 'size-7',
        md: 'size-9',
        lg: 'size-11',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
)

interface IconChipProps extends VariantProps<typeof chipStyles> {
  children: ReactNode
  className?: string
}

export const IconChip = ({ children, tone, size, className }: IconChipProps) => (
  <span className={cn(chipStyles({ tone, size }), className)} aria-hidden>
    {children}
  </span>
)

// ===================================================================
// سربرگ صفحه و بخش
// ===================================================================

interface PageHeaderProps {
  title: string
  caption?: string
  icon?: ReactNode
  actions?: ReactNode
  /** نشانه‌ی کوچک بالای عنوان، مثل نام مرحله یا حالت اجرا. */
  eyebrow?: string
  className?: string
}

/**
 * سربرگ صفحه: عنوان بزرگ، توضیح یک‌خطی، ناحیه‌ی عمل در سمت مقابل.
 *
 * عنوان صفحه با عنوان بخش یکی نیست: این `h1` است و مقیاس بزرگ‌تری دارد،
 * وگرنه سلسله‌مراتب صفحه تخت می‌شود.
 */
export const PageHeader = ({
  title,
  caption,
  icon,
  actions,
  eyebrow,
  className,
}: PageHeaderProps) => (
  <header
    className={cn('animate-rise flex flex-wrap items-end justify-between gap-4 pb-5', className)}
  >
    <div className="flex min-w-0 items-center gap-3.5">
      {icon !== undefined && (
        <IconChip tone="solid" size="lg">
          {icon}
        </IconChip>
      )}
      <div className="min-w-0">
        {eyebrow !== undefined && eyebrow !== '' && (
          <p className="text-[11px] font-semibold tracking-wide text-accent">{eyebrow}</p>
        )}
        <h1 className="truncate text-xl font-bold tracking-tight text-ink sm:text-2xl">
          {title}
        </h1>
        {caption !== undefined && caption !== '' && (
          <p className="mt-1 text-sm leading-relaxed text-ink-muted">{caption}</p>
        )}
      </div>
    </div>
    {actions !== undefined && (
      <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
    )}
  </header>
)

interface SectionHeaderProps {
  title: string
  caption?: string
  icon?: ReactNode
  actions?: ReactNode
  className?: string
}

export const SectionHeader = ({
  title,
  caption,
  icon,
  actions,
  className,
}: SectionHeaderProps) => (
  <header
    className={cn(
      'flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4',
      className,
    )}
  >
    <div className="flex min-w-0 items-center gap-3">
      {icon !== undefined && (
        <IconChip tone="accent" size="sm">
          {icon}
        </IconChip>
      )}
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-ink">{title}</h2>
        {caption !== undefined && caption !== '' && (
          <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">{caption}</p>
        )}
      </div>
    </div>
    {actions !== undefined && (
      <div className="flex shrink-0 items-center gap-2">{actions}</div>
    )}
  </header>
)

// ===================================================================
// badge
// ===================================================================

const badgeStyles = cva(
  [
    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5',
    'text-xs font-medium whitespace-nowrap ring-1 ring-inset',
  ],
  {
    variants: {
      tone: {
        neutral: 'bg-neutral-soft text-ink-muted ring-line',
        accent: 'bg-accent-soft text-accent ring-accent-line',
        support: 'bg-support-soft text-support ring-support/25',
        oppose: 'bg-oppose-soft text-oppose ring-oppose/25',
        conditional: 'bg-conditional-soft text-conditional ring-conditional/30',
      },
      // نگه داشته شد تا امضای قبلی نشکند؛ حالا هر badge حلقه‌ی نازک دارد.
      outline: { true: '', false: '' },
    },
    defaultVariants: { tone: 'neutral', outline: false },
  },
)

interface BadgeProps
  extends ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof badgeStyles> {
  /** نقطه‌ی رنگی کوچک. برای وضعیت زنده/متوقف. */
  dot?: boolean
}

export const Badge = ({
  className,
  tone,
  outline,
  dot = false,
  children,
  ...rest
}: BadgeProps) => (
  <span className={cn(badgeStyles({ tone, outline }), className)} {...rest}>
    {dot && (
      // حلقه‌ی دور نقطه، آن را روی هر زمینه‌ای خوانا می‌کند.
      <span className="size-1.5 rounded-full bg-current ring-2 ring-current/25" aria-hidden />
    )}
    {children}
  </span>
)

// ===================================================================
// بنر
// ===================================================================

const bannerIcons = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
} as const

const bannerStyles = cva(
  'flex items-start gap-3 rounded-md border p-3.5 text-sm shadow-raised',
  {
    variants: {
      tone: {
        info: 'border-accent-line bg-accent-soft/60 text-ink',
        success: 'border-support/25 bg-support-soft/60 text-ink',
        warning: 'border-conditional/30 bg-conditional-soft/60 text-ink',
        danger: 'border-oppose/25 bg-oppose-soft/60 text-ink',
      },
    },
    defaultVariants: { tone: 'info' },
  },
)

const bannerChipTone = {
  info: 'accent',
  success: 'support',
  warning: 'conditional',
  danger: 'oppose',
} as const

interface BannerProps extends VariantProps<typeof bannerStyles> {
  children: ReactNode
  className?: string
  /** عنوان اختیاری، وقتی متن چند جمله است. */
  title?: string
}

export const Banner = ({ tone = 'info', title, children, className }: BannerProps) => {
  const key = tone ?? 'info'
  const Icon = bannerIcons[key]
  return (
    <div
      className={cn(bannerStyles({ tone }), className)}
      // خطا باید بی‌درنگ اعلام شود؛ بقیه در نوبت خودشان.
      role={key === 'danger' ? 'alert' : 'status'}
    >
      <IconChip tone={bannerChipTone[key]} size="sm">
        <Icon className="size-4" />
      </IconChip>
      <div className="min-w-0 flex-1 leading-relaxed">
        {title !== undefined && <p className="font-semibold">{title}</p>}
        <div className={cn(title !== undefined && 'mt-1 text-ink-muted')}>{children}</div>
      </div>
    </div>
  )
}

// ===================================================================
// سنجه‌ها
// ===================================================================

interface KpiProps {
  label: string
  value: string
  hint?: string
  attention?: boolean
  icon?: ReactNode
}

/**
 * سنجه‌ی عددی. عدد با مقیاس بزرگ و وزن سنگین می‌آید تا در یک نگاه خوانده شود؛
 * برچسب کوچک بالای آن است، نه هم‌اندازه‌اش.
 */
export const Kpi = ({ label, value, hint, attention = false, icon }: KpiProps) => (
  <div
    className={cn(
      'group relative overflow-hidden rounded-md border border-line bg-surface-raised px-4 py-3.5',
      'shadow-raised transition-colors',
      attention && 'border-conditional/40 bg-conditional-soft/50',
    )}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs font-medium text-ink-muted">{label}</p>
      {icon !== undefined && (
        <span
          className="text-ink-faint transition-colors group-hover:text-accent"
          aria-hidden
        >
          {icon}
        </span>
      )}
    </div>
    <p className="mt-1.5 text-2xl leading-tight font-bold tracking-tight tabular-nums text-ink">
      {value}
    </p>
    {hint !== undefined && hint !== '' && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
  </div>
)

export const KpiRow = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{children}</div>
)

interface MeterProps {
  label: string
  value: number
  tone?: 'support' | 'conditional' | 'oppose' | 'accent'
}

/** سنجه‌ی افقی درصدی. برای اطمینان مشاور و سهم امتیازها. */
export const Meter = ({ label, value, tone = 'accent' }: MeterProps) => {
  const clamped = Math.max(0, Math.min(100, value))
  const bar = {
    support: 'from-support to-support',
    conditional: 'from-conditional to-conditional',
    oppose: 'from-oppose to-oppose',
    accent: 'from-accent to-accent-two',
  }[tone]
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-ink-muted">{label}</span>
        <span className="font-semibold tabular-nums text-ink">{faPercent(clamped)}</span>
      </div>
      <div
        className="mt-2 h-2 overflow-hidden rounded-full bg-surface-sunken ring-1 ring-inset ring-line"
        role="meter"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        {/* عرض inline است چون مقدارش داده است و کلاس Tailwind نمی‌تواند
            درصد دلخواه بسازد. */}
        <div
          className={cn(
            'h-full rounded-full bg-linear-100 transition-[width] duration-500',
            bar,
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  )
}

interface RingProps {
  value: number
  label?: string
  size?: number
}

/**
 * حلقه‌ی درصدی برای اطمینان.
 *
 * با `conic-gradient` ساخته می‌شود نه SVG: یک عنصر، بدون viewBox و بدون
 * محاسبه‌ی محیط دایره. شروع از `-90deg` است تا از بالا پر شود.
 */
export const Ring = ({ value, label, size = 84 }: RingProps) => {
  const clamped = Math.max(0, Math.min(100, value))
  const turn = clamped / 100
  return (
    <div
      className="relative grid shrink-0 place-items-center"
      style={{ width: size, height: size }}
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'درصد'}
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(from -90deg, var(--color-accent), var(--color-accent-two) ${turn}turn, var(--color-surface-sunken) ${turn}turn)`,
        }}
        aria-hidden
      />
      <div
        className="absolute rounded-full bg-surface-raised ring-1 ring-line"
        style={{ inset: Math.max(6, Math.round(size * 0.1)) }}
        aria-hidden
      />
      <div className="relative text-center leading-none">
        <span className="text-lg font-bold tabular-nums text-ink">{faPercent(clamped)}</span>
        {label !== undefined && (
          <span className="mt-1 block text-[10px] text-ink-faint">{label}</span>
        )}
      </div>
    </div>
  )
}

// ===================================================================
// ردیف‌های جزئیات
// ===================================================================

export const DetailRows = ({ rows }: { rows: [string, string][] }) => (
  <dl className="divide-y divide-line">
    {rows.map(([label, value]) => (
      <div
        key={label}
        className="grid grid-cols-[9rem_1fr] gap-3 px-1 py-2.5 text-sm transition-colors hover:bg-surface-sunken/60"
      >
        <dt className="text-ink-muted">{label}</dt>
        <dd className="min-w-0 leading-relaxed font-medium break-words text-ink">{value}</dd>
      </div>
    ))}
  </dl>
)

// ===================================================================
// فهرست نشانه‌دار
// ===================================================================

interface BulletsProps {
  title?: string
  items: string[]
  icon?: ReactNode
}

/** فهرست خالی چیزی رندر نمی‌کند، تا کارت‌های تهی در نتیجه دیده نشوند. */
export const Bullets = ({ title, items, icon }: BulletsProps) => {
  const kept = items.filter((item) => item.trim() !== '')
  if (kept.length === 0) {
    return null
  }
  return (
    <div>
      {title !== undefined && (
        <h3 className="flex items-center gap-2 text-xs font-semibold text-ink-muted">
          {icon}
          {title}
        </h3>
      )}
      <ul className="mt-2.5 space-y-2">
        {kept.map((item, index) => (
          <li
            key={`${index}-${item.slice(0, 24)}`}
            className="flex gap-2.5 text-sm leading-relaxed"
          >
            <span
              className="mt-2 size-1.5 shrink-0 rounded-full bg-linear-100 from-accent to-accent-two"
              aria-hidden
            />
            <span className="min-w-0">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ===================================================================
// حالت خالی
// ===================================================================

interface EmptyStateProps {
  title: string
  detail?: string
  icon?: ReactNode
  action?: ReactNode
}

export const EmptyState = ({ title, detail, icon, action }: EmptyStateProps) => (
  <div className="relative flex flex-col items-center gap-3 overflow-hidden px-6 py-14 text-center">
    {/* شبکه‌ی نقطه‌چین محو، تا ناحیه‌ی خالی «طراحی‌شده» دیده شود نه «فراموش‌شده». */}
    <span
      className="dot-grid pointer-events-none absolute inset-0 opacity-40 [mask-image:radial-gradient(60%_60%_at_50%_45%,black,transparent)]"
      aria-hidden
    />
    {icon !== undefined && (
      <IconChip tone="neutral" size="lg" className="relative">
        {icon}
      </IconChip>
    )}
    <p className="relative text-sm font-semibold text-ink">{title}</p>
    {detail !== undefined && (
      <p className="relative max-w-md text-xs leading-relaxed text-ink-muted">{detail}</p>
    )}
    {action !== undefined && <div className="relative mt-1">{action}</div>}
  </div>
)

// ===================================================================
// بارگذاری
// ===================================================================

/**
 * جای متن در حال بارگذاری، با درخشش عبوری.
 *
 * `animate-pulse` تنها، حس «یخ‌زده» می‌داد؛ درخشش عبوری نشان می‌دهد چیزی در
 * جریان است. اندازه‌اش را از کلاس می‌گیرد.
 */
export const Skeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-md bg-surface-sunken',
      'after:animate-sweep after:absolute after:inset-0 after:bg-linear-100',
      'after:from-transparent after:via-surface-raised/70 after:to-transparent',
      className,
    )}
    aria-hidden
  />
)

export const LoadingRows = ({ rows = 3 }: { rows?: number }) => (
  <div className="space-y-2.5 p-5" role="status" aria-label="در حال بارگذاری">
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton
        key={index}
        className="h-10 w-full"
        // هر ردیف کمی دیرتر شروع می‌کند تا موج، جهت‌دار دیده شود.
      />
    ))}
  </div>
)

// ===================================================================
// شماره‌ی فارسی برای فهرست‌های مرتب
// ===================================================================

export const OrdinalBadge = ({ value }: { value: number }) => (
  <span
    className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-accent-soft text-xs font-bold tabular-nums text-accent ring-1 ring-inset ring-accent-line"
    aria-hidden
  >
    {fa(value)}
  </span>
)

// ===================================================================
// نوار فعالیت — کار پس‌زمینه‌ی در حال اجرا
// ===================================================================

/**
 * نشانگر کار در جریان، با نوار پیشرفت نامعین.
 *
 * پیشرفت **نامعین** است چون سرور درصد نمی‌دهد، فقط نام گام را می‌دهد. نوار
 * جعلیِ درصددار بدتر از نوار نامعین است: به کاربر عددی می‌دهد که معنایی ندارد.
 */
export const ActivityStrip = ({ children }: { children: ReactNode }) => (
  <div
    className="relative overflow-hidden rounded-md border border-accent-line bg-accent-soft/60 p-4"
    role="status"
  >
    <div className="flex items-center gap-3 text-sm">
      <span
        className="grid size-7 shrink-0 place-items-center rounded-full bg-surface-raised text-accent shadow-raised"
        aria-hidden
      >
        <span className="size-3.5 animate-spin rounded-full border-2 border-accent/25 border-t-accent" />
      </span>
      <span className="min-w-0 flex-1 font-medium text-ink">{children}</span>
    </div>
    <span className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-accent/15" aria-hidden>
      <span className="animate-sweep block h-full w-2/5 bg-linear-100 from-accent to-accent-two" />
    </span>
  </div>
)
